import React, { useState } from 'react';

import { META_TYPE, META_NUM_OPERATOR, META_STR_OPERATOR } from '@vlabs/api-bindings/src/constants';
import { DeleteIcon } from '@vlabs/icons';
import validate from '@vlabs/shared/validators';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import { Input } from '@vlabs/uikit/components/controls/input';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import type { SelectProps } from '@vlabs/uikit/components/controls/select/Select';

import type { Option } from '@vlabs/monokit/shared/ui/options-filter';

import type { MetaFilterItem } from './serialization';
import { useEventListFiltersTranslation } from '../../../i18n';
import st from '../../popovers.module.sass';

const findOption = (options: Option[], value: string | undefined): Option | undefined => (
  options.find((option) => option.value === value)
);

const asError = (result: unknown): string | undefined => (
  typeof result === 'string' ? result : undefined
);

const requiredError = (): string => String(validate.required());

const getKeyError = (value: string): string | undefined => (
  value ? asError(validate.metaKey()(value)) : requiredError()
);

const getValueError = (value: string, type?: string): string | undefined => (
  value ? asError(validate.metaValue(type)(value)) : requiredError()
);

const getRequiredError = (value?: string): string | undefined => (
  value ? undefined : requiredError()
);

interface MetaFilterRowProps {
  item: MetaFilterItem;
  index: number;
  onChange: (index: number, patch: Partial<MetaFilterItem>) => void;
  onRemove: (index: number) => void;
}

export function MetaFilterRow({
  item, index, onChange, onRemove,
}: MetaFilterRowProps): React.JSX.Element {
  const { t } = useEventListFiltersTranslation();

  const [keyError, setKeyError] = useState<string>();
  const [valueError, setValueError] = useState<string>();
  const [typeError, setTypeError] = useState<string>();
  const [operatorError, setOperatorError] = useState<string>();

  const operatorOptions = item.type === 'string' ? META_STR_OPERATOR.raw : META_NUM_OPERATOR.raw;

  const handleKeyChange = (value: string) => {
    onChange(index, { key: value });
    setKeyError(getKeyError(value));
  };

  const handleKeyClear = () => {
    onChange(index, { key: '' });
    setKeyError(getKeyError(''));
  };

  const handleValueChange = (value: string) => {
    onChange(index, { value });
    setValueError(getValueError(value, item.type));
  };

  const handleValueClear = () => {
    onChange(index, { value: '' });
    setValueError(getValueError('', item.type));
  };

  const handleTypeChange = (option: Option | null) => {
    onChange(index, { type: option?.value, operator: undefined });
    setTypeError(getRequiredError(option?.value));
    setOperatorError(undefined);
    if (item.value) setValueError(getValueError(item.value, option?.value));
  };

  const handleOperatorChange = (option: Option | null) => {
    onChange(index, { operator: option?.value });
    setOperatorError(getRequiredError(option?.value));
  };

  return (
    <React.Fragment>
      <div className={st.Flex}>
        <span className="Subtitle-1">{t('meta')}</span>
        <IconButton
          data-testid="meta.deleteButton"
          icon={<DeleteIcon />}
          kind="negative"
          onClick={() => onRemove(index)}
          title={t('meta_delete')}
          variant="filled"
        />
      </div>

      <div>
        <Input
          errorMessage={keyError}
          hasError={!!keyError}
          id={`meta.${index}.key`}
          isClearable
          label={t('meta_key')}
          name={`meta.${index}.key`}
          onChangeValue={handleKeyChange}
          onClear={handleKeyClear}
          value={item.key}
        />
      </div>

      <div>
        <Input
          errorMessage={valueError}
          hasError={!!valueError}
          id={`meta.${index}.value`}
          isClearable
          label={t('meta_value')}
          name={`meta.${index}.value`}
          onChangeValue={handleValueChange}
          onClear={handleValueClear}
          value={item.value}
        />
      </div>

      <div>
        <Select
          errorMessage={typeError}
          hasError={!!typeError}
          id={`meta.${index}.type`}
          isClearable
          label={t('meta_type')}
          name={`meta.${index}.type`}
          onChange={handleTypeChange as SelectProps['onChange']}
          options={META_TYPE.raw}
          value={findOption(META_TYPE.raw, item.type)}
        />
      </div>

      <div>
        <Select
          disabled={!item.type}
          errorMessage={operatorError}
          hasError={!!operatorError}
          id={`meta.${index}.operator`}
          isClearable
          label={t('meta_operator')}
          name={`meta.${index}.operator`}
          onChange={handleOperatorChange as SelectProps['onChange']}
          options={operatorOptions}
          value={findOption(operatorOptions, item.operator)}
        />
      </div>
    </React.Fragment>
  );
}
